import { buildEstimatedRouteLegs, effectiveRouteLegs, routeDistanceMethod } from "./domain";
import {
  ORS_PROVIDER_ID,
  ROUTE_API_PATH,
  type RoadRouteMode,
  type RouteApiLegData,
  type RouteApiLegRequest,
  type RouteApiResponse,
} from "./routeApi";
import type {
  AccommodationNode,
  CoordinatePair,
  ItineraryStop,
  RouteLeg,
  RouteRefreshCounts,
  RouteRefreshResult,
  TransportMode,
  Trip,
} from "./types";

const GOOGLE_MAPS_DIRECTIONS_URL = "https://www.google.com/maps/dir/";

function routeServiceUrl(): string {
  if (typeof window !== "undefined" && window.location.protocol === "roam:") {
    return `https://roam.codepawl.com${ROUTE_API_PATH}`;
  }
  return ROUTE_API_PATH;
}

function isCoordinatePair(value: unknown): value is CoordinatePair {
  return Array.isArray(value)
    && value.length === 2
    && typeof value[0] === "number"
    && Number.isFinite(value[0])
    && value[0] >= -180
    && value[0] <= 180
    && typeof value[1] === "number"
    && Number.isFinite(value[1])
    && value[1] >= -90
    && value[1] <= 90;
}

function validLegData(value: unknown): value is RouteApiLegData {
  if (!value || typeof value !== "object") return false;
  const data = value as Partial<RouteApiLegData>;
  return typeof data.id === "string"
    && typeof data.fingerprint === "string"
    && typeof data.distanceKm === "number"
    && Number.isFinite(data.distanceKm)
    && data.distanceKm >= 0
    && typeof data.durationMinutes === "number"
    && Number.isFinite(data.durationMinutes)
    && data.durationMinutes >= 0
    && Array.isArray(data.geometry)
    && data.geometry.length >= 2
    && data.geometry.every(isCoordinatePair)
    && data.providerId === ORS_PROVIDER_ID
    && typeof data.fetchedAt === "string"
    && typeof data.expiresAt === "string"
    && typeof data.cached === "boolean";
}

async function requestRoutes(legs: RouteApiLegRequest[], signal?: AbortSignal, force = false): Promise<RouteApiResponse> {
  const response = await fetch(routeServiceUrl(), {
    method: "POST",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify({ legs, locale: typeof navigator === "undefined" ? "en" : navigator.language, force }),
    signal,
  });
  const payload: unknown = await response.json().catch(() => null);
  if (!payload || typeof payload !== "object" || !("ok" in payload)) {
    throw new Error("Route service returned an invalid response.");
  }
  return payload as RouteApiResponse;
}

type RouteNode = ItineraryStop | (AccommodationNode & { id: string; lat: number; lon: number });

function routeNode(trip: Trip, id: string): RouteNode | null {
  const stop = trip.stops.find((candidate) => candidate.id === id);
  if (stop) return stop;
  for (const day of trip.days) {
    const accommodation = day.accommodation;
    if (accommodation?.id === id && Number.isFinite(accommodation.lat) && Number.isFinite(accommodation.lon)) {
      return accommodation as RouteNode;
    }
  }
  return null;
}

function toRequest(trip: Trip, leg: RouteLeg): RouteApiLegRequest | null {
  if (leg.transportMode !== "walk" && leg.transportMode !== "bike" && leg.transportMode !== "drive") return null;
  const from = routeNode(trip, leg.fromStopId);
  const to = routeNode(trip, leg.toStopId);
  if (!from || !to) return null;
  return {
    id: leg.id,
    fingerprint: leg.fingerprint,
    mode: leg.transportMode as RoadRouteMode,
    from: [from.lon, from.lat],
    to: [to.lon, to.lat],
  };
}

function providerLeg(expected: RouteLeg, data: RouteApiLegData): RouteLeg {
  return {
    ...expected,
    distanceKm: Number(data.distanceKm.toFixed(2)),
    durationMinutes: Math.max(1, Math.ceil(data.durationMinutes)),
    geometry: data.geometry,
    source: data.cached ? "cached" : "provider",
    distanceMethod: "road_network",
    durationMethod: "provider",
    providerId: data.providerId,
    fetchedAt: data.fetchedAt,
    expiresAt: data.expiresAt,
    fallbackReason: undefined,
  };
}

function countsFor(legs: RouteLeg[]): RouteRefreshCounts {
  return legs.reduce<RouteRefreshCounts>((counts, leg) => {
    if (leg.source === "provider") counts.fresh += 1;
    else if (leg.source === "cached") counts.cached += 1;
    else if (leg.source === "planned") counts.planned += 1;
    else counts.estimated += 1;
    return counts;
  }, { fresh: 0, cached: 0, estimated: 0, planned: 0 });
}

export async function refreshTripRouteLegs(
  trip: Trip,
  options: { dayId?: string; force?: boolean; signal?: AbortSignal } = {},
): Promise<RouteRefreshResult> {
  const now = new Date();
  const effective = effectiveRouteLegs(trip, now);
  const expected = buildEstimatedRouteLegs(trip, now);
  const scope = expected.filter((leg) => !options.dayId || leg.dayId === options.dayId);
  const currentById = new Map(effective.map((leg) => [leg.id, leg]));
  const requests = scope.flatMap((fallback) => {
    const current = currentById.get(fallback.id) ?? fallback;
    if (!options.force && current.source === "provider" && (!current.expiresAt || Date.parse(current.expiresAt) > now.getTime())) return [];
    const request = toRequest(trip, fallback);
    return request ? [request] : [];
  });
  const warnings: string[] = scope
    .filter((leg) => leg.source === "planned" && leg.durationMinutes === 0)
    .map((leg) => {
      const from = routeNode(trip, leg.fromStopId);
      const to = routeNode(trip, leg.toStopId);
      return `${leg.transportMode === "transit" ? "Transit" : "Travel"} time required from ${from?.name ?? "the current stop"} to ${to?.name ?? "the next stop"}.`;
    });

  const replacements = new Map<string, RouteLeg>();
  if (requests.length) {
    for (let index = 0; index < requests.length; index += 12) {
      const batch = requests.slice(index, index + 12);
      try {
        const response = await requestRoutes(batch, options.signal, options.force === true);
        if (!response.ok) throw new Error(response.error.message);
        for (const result of response.data.legs) {
          const fallback = scope.find((leg) => leg.id === (result.ok ? result.data.id : result.id));
          if (!fallback) continue;
          const current = currentById.get(fallback.id) ?? fallback;
          if (result.ok && validLegData(result.data) && result.data.fingerprint === fallback.fingerprint) {
            replacements.set(fallback.id, providerLeg(fallback, result.data));
          } else if (!result.ok) {
            const preserved = current.source === "provider" || current.source === "cached"
              ? { ...current, source: "cached" as const, fallbackReason: result.error.message }
              : { ...fallback, fallbackReason: result.error.message };
            replacements.set(fallback.id, preserved);
            warnings.push(result.error.message);
          }
        }
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") throw error;
        const message = error instanceof Error ? error.message : "Route service is unavailable.";
        warnings.push(message);
        for (const request of batch) {
          const fallback = scope.find((leg) => leg.id === request.id);
          if (!fallback) continue;
          const current = currentById.get(fallback.id) ?? fallback;
          replacements.set(fallback.id, current.source === "provider" || current.source === "cached"
            ? { ...current, source: "cached", fallbackReason: message }
            : { ...fallback, fallbackReason: message });
        }
      }
    }
  }

  const refreshedScope = scope.map((leg) => replacements.get(leg.id) ?? currentById.get(leg.id) ?? leg);
  return {
    tripId: trip.id,
    ...(options.dayId ? { dayId: options.dayId } : {}),
    routeLegs: refreshedScope,
    counts: countsFor(refreshedScope),
    warnings: [...new Set(warnings)],
    distanceMethod: routeDistanceMethod(refreshedScope),
  };
}

export function applyRouteRefresh(trip: Trip, incoming: RouteLeg[]): Trip {
  const expectedById = new Map(buildEstimatedRouteLegs(trip).map((leg) => [leg.id, leg]));
  const replacements = new Map(incoming
    .filter((leg) => expectedById.get(leg.id)?.fingerprint === leg.fingerprint)
    .map((leg) => [leg.id, leg]));
  return {
    ...trip,
    routeLegs: effectiveRouteLegs(trip).map((leg) => replacements.get(leg.id) ?? leg),
  };
}

export function googleMapsDirectionsUrl(
  to: Pick<RouteNode, "lat" | "lon">,
  transportMode: TransportMode,
  from?: Pick<RouteNode, "lat" | "lon">,
): string {
  const url = new URL(GOOGLE_MAPS_DIRECTIONS_URL);
  url.searchParams.set("api", "1");
  if (from) url.searchParams.set("origin", `${from.lat},${from.lon}`);
  url.searchParams.set("destination", `${to.lat},${to.lon}`);
  const mode = transportMode === "walk" ? "walking"
    : transportMode === "bike" ? "bicycling"
      : transportMode === "drive" ? "driving"
        : transportMode === "transit" ? "transit"
          : null;
  if (mode) url.searchParams.set("travelmode", mode);
  url.searchParams.set("dir_action", "navigate");
  return url.toString();
}

export function routeLegNavigationUrl(trip: Trip, leg: RouteLeg): string | null {
  const from = routeNode(trip, leg.fromStopId);
  const to = routeNode(trip, leg.toStopId);
  return to ? googleMapsDirectionsUrl(to, leg.transportMode, from ?? undefined) : null;
}
